// PayrollProcess

import { useCallback, useEffect, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_DEPARTMENT, EMPLOYEE_TYPE, PAYROLL_PERIOD, PAYROLL_RUN_GET, PAYROLL_SHORT_DUTY_REVIEW } from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { Eye, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import moment from "moment";
import useApiMethods from "../../lib/useApiMethods";
import { DialogModal } from "../../components/ui/dialogModal";
import { DatePicker, Select } from "antd";
import { Input } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";

const { Option } = Select;

export const disabledFutureDate = (current) => {
    return current && current > moment().endOf('day');
};

const PayrollProcess = () => {
    const { fetch, create } = useApiMethods();
    const { add: addPermission, edit: editPermission, active: isActive } = checkUserPermissionFunc("payroll", "Payroll", "SubMenu");

    const [payrollProcessData, setPayrollProcessData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);

    const [dropdownList, setDropdownList] = useState({
        employeeTypeList: [],
        departmentList: [],
        payrollPeriodList: [],
    });

    const navigate = useNavigate();

    // Form state
    const [formData, setFormData] = useState({
        payroll_date: moment().format("DD-MM-YYYY HH:mm:ss"),
        payroll_month_year: "",
        from_date: "",
        to_date: "",
        employee_type: [],
        department: [],
        payroll_period: "",
        total_days: ""
    });

    // Reset form & state
    const resetForm = () => {
        setFormData({
            payroll_date: moment().format("DD-MM-YYYY HH:mm:ss"),
            payroll_month_year: "",
            from_date: "",
            to_date: "",
            employee_type: [],
            department: [],
            payroll_period: "",
            total_days: ""
        });
        setOpenDialog(false);
    };

    // Fetch dropdown data
    const fetchDropdownData = useCallback(async () => {
        try {
            const [employeeTypeRes, departmentRes, payrollPeriodRes] = await Promise.all([
                fetch(EMPLOYEE_TYPE, { drop_down: 'True' }),
                fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' }),
                fetch(PAYROLL_PERIOD, { drop_down: 'True' })
            ]);

            setDropdownList({
                employeeTypeList: employeeTypeRes?.status === "success" ? employeeTypeRes.data : [],
                departmentList: departmentRes?.status === "success" ? departmentRes.data : [],
                payrollPeriodList: payrollPeriodRes?.status === "success" ? payrollPeriodRes.data : [],
            });
        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            ToastMsg('error', 'Failed to load dropdown data');
        }
    }, [fetch]);

    useEffect(() => {
        fetchDropdownData();
    }, []);

    // Capitalize function
    const capitalize = (str) => {
        if (!str) return "-";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Status chip styling function
    const getStatusChipStyle = (status) => {
        const statusLower = status?.toLowerCase();

        switch (statusLower) {
            case 'pending':
                return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700";
            case 'completed':
                return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 border border-green-200 dark:border-green-700";
            case 'processing':
                return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-700";
            case 'failed':
                return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border border-red-200 dark:border-red-700";
            case 'draft':
                return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
            default:
                return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
        }
    };

    // Calculate from and to dates based on payroll period
    const calculateFromAndToDate = (payrollPeriod, payrollMonthYear) => {
        if (!payrollPeriod || !payrollMonthYear) return;

        const selectedPeriod = dropdownList.payrollPeriodList.find((item) => item.id === payrollPeriod);
        if (!selectedPeriod) return;

        const { start_day, end_day, month: selectedMonths, period } = selectedPeriod;
        const parsedDate = moment(payrollMonthYear, "MMMM YYYY");

        if (!parsedDate.isValid()) return;

        const currentMonth = parsedDate.month() + 1;
        const currentYear = parsedDate.year();

        let newFromDate = null;
        let newToDate = null;
        let newTotalDays = 0;

        if (period === "month_wise") {
            const daysInCurrentMonth = moment(`${currentYear}-${currentMonth}`, 'YYYY-MM').daysInMonth();
            const startDay = Math.min(start_day, daysInCurrentMonth);
            const endDay = Math.min(end_day, daysInCurrentMonth);

            newFromDate = moment(`${currentYear}-${currentMonth}-${startDay}`, 'YYYY-MM-DD').format('DD-MM-YYYY');
            newToDate = moment(`${currentYear}-${currentMonth}-${endDay}`, 'YYYY-MM-DD').format('DD-MM-YYYY');
            newTotalDays = endDay - startDay + 1;
        } else if (period === "customize") {
            const previousMonth = parsedDate.clone().subtract(1, 'month');
            const payrollMonth = previousMonth.month() + 1;
            const payrollYear = previousMonth.year();
            const daysInSelectedMonth = moment(`${payrollYear}-${payrollMonth}`, 'YYYY-MM').daysInMonth();

            if (selectedMonths.includes(currentMonth) || selectedMonths.includes(0)) {
                const adjustedStartDay = Math.min(start_day, daysInSelectedMonth);
                const adjustedEndDay = Math.min(end_day, daysInSelectedMonth);
                let startDate = moment(`${payrollYear}-${payrollMonth}-${adjustedStartDay}`, 'YYYY-MM-DD');
                let endDate = moment(`${payrollYear}-${payrollMonth}-${adjustedEndDay}`, 'YYYY-MM-DD');

                if (endDate.isBefore(startDate)) {
                    endDate = endDate.add(1, 'month');
                }
                newFromDate = startDate.format('DD-MM-YYYY');
                newToDate = endDate.format('DD-MM-YYYY');
                newTotalDays = endDate.diff(startDate, 'days') + 1;
            } else {
                ToastMsg("error", 'To run the payroll, please ensure that the payroll period has been configured');
            }
        }

        setFormData(prev => ({
            ...prev,
            from_date: newFromDate,
            to_date: newToDate,
            total_days: newTotalDays
        }));
    };

    // Handle form field changes
    const handleChange = (name, value) => {
        switch (name) {
            case "department":
            case "employee_type":
                setFormData(prev => ({
                    ...prev,
                    [name]: value.includes('all') ? ["all"] : value
                }));
                break;
            case "payroll_month_year":
                setFormData(prev => ({ ...prev, payroll_month_year: value }));
                calculateFromAndToDate(formData.payroll_period, value);
                break;
            case "payroll_period":
                setFormData(prev => ({ ...prev, payroll_period: value }));
                calculateFromAndToDate(value, formData.payroll_month_year);
                break;
            default:
                setFormData(prev => ({ ...prev, [name]: value }));
                break;
        }
    };

    // Prepare payload for API
    const preparePayload = () => {
        const payrollDate = moment().format("YYYY-MM-DD HH:mm:ss");
        const payrollMonth = formData.payroll_month_year ? moment(formData.payroll_month_year, "MMMM YYYY").month() + 1 : null;
        const payrollYear = formData.payroll_month_year ? moment(formData.payroll_month_year, "MMMM YYYY").year() : null;

        // Handle "all" selection for departments and employee types
        let departments = formData.department;
        let employeeTypes = formData.employee_type;

        // If "all" is selected, get all IDs from the dropdown lists
        if (departments.includes("all")) {
            departments = dropdownList.departmentList.map(dept => dept.id);
        }

        if (employeeTypes.includes("all")) {
            employeeTypes = dropdownList.employeeTypeList.map(type => type.id);
        }

        // Convert date formats from DD-MM-YYYY to YYYY-MM-DD
        const fromDate = formData.from_date ? moment(formData.from_date, "DD-MM-YYYY").format("YYYY-MM-DD") : null;
        const toDate = formData.to_date ? moment(formData.to_date, "DD-MM-YYYY").format("YYYY-MM-DD") : null;

        return {
            payroll_date: payrollDate,
            payroll_month: payrollMonth,
            payroll_year: payrollYear,
            department: departments,
            employee_type: employeeTypes,
            payroll_period: parseInt(formData.payroll_period) || null,
            from_date: fromDate,
            to_date: toDate,
            total_days: parseInt(formData.total_days) || 0
        };
    };

    // Save payroll process
    const handleSave = async () => {
        // Validate required fields
        if (!formData.payroll_month_year) {
            ToastMsg("error", "Please select Payroll Month & Year");
            return;
        }

        if (!formData.payroll_period) {
            ToastMsg("error", "Please select Payroll Period");
            return;
        }

        if (!formData.from_date || !formData.to_date) {
            ToastMsg("error", "Payroll period dates are not calculated. Please check your selections.");
            return;
        }

        setLoading(true);
        try {
            const payload = preparePayload();

            // Only open the Short-Duty Review screen when the company has short-duty
            // deduction enabled (Settings) AND there are missing-punch days to review.
            // Otherwise the review adds nothing, so run payroll directly.
            const review = await fetch(PAYROLL_SHORT_DUTY_REVIEW, {
                from_date: payload.from_date,
                to_date: payload.to_date,
                department: payload.department,
                employee_type: payload.employee_type,
            });
            const enabled = !!review?.data?.config?.enabled;
            const count = review?.data?.count || 0;

            if (enabled && count > 0) {
                // Review gate — that screen finalises deductions then creates the payroll.
                resetForm();
                navigate('/payroll/shortdutyreview', { state: { payload, review: review.data } });
                return;
            }

            // Disabled or nothing to review → create the payroll now.
            const response = await create(PAYROLL_RUN_GET, payload);
            if (response?.status === "success") {
                ToastMsg("success", "Payroll process started successfully");
                resetForm();
                navigate('/payroll/runpayrollprocess', { state: { PaySlipId: response.data } });
            } else {
                ToastMsg("error", response?.message || "Failed to start payroll process");
            }
        } catch (error) {
            console.error("Error starting payroll process:", error);
            ToastMsg("error", error.message || "Failed to start payroll process");
        } finally {
            setLoading(false);
        }
    };

    const handleRunPayroll = () => {
        setOpenDialog(true);
    };

    const PayrollProcessColumns = [
        {
            accessor: "payroll_date",
            Header: "Payroll Date",
            sortable: true,
            filterable: true,
            filterType: 'date',
            Cell: ({ value }) => {
                if (!value) return "-";
                return moment(value).format("DD-MM-YYYY");
            }
        },
        {
            accessor: "payroll_month",
            Header: "Payroll Month",
            sortable: true,
            filterable: true,
            filterType: 'month',
            Cell: ({ value }) => {
                const payrollMonth = value ? moment().month(value - 1).format('MMMM') : "";
                return (
                    <p className="text-gray-900 dark:text-white">
                        {payrollMonth ? `Payroll For ${payrollMonth}` : "-"}
                    </p>
                );
            }
        },
        {
            accessor: "employee_type",
            Header: "Employee Type",
            Cell: ({ row }) => {
                const employeeTypes = row?.employee_type;
                let employeeTypeText = "-";

                if (Array.isArray(employeeTypes) && employeeTypes.length > 0) {
                    employeeTypeText = employeeTypes
                        .map(type => type?.employee_type_name || type?.name || "")
                        .filter(Boolean)
                        .join(", ");
                } else if (typeof employeeTypes === 'string') {
                    employeeTypeText = employeeTypes;
                } else if (employeeTypes && typeof employeeTypes === 'object') {
                    employeeTypeText = employeeTypes.employee_type_name || employeeTypes.name || "-";
                }

                return (
                    <span className="text-gray-700 dark:text-gray-300">
                        {truncateText(employeeTypeText, 20, 'balance', 'No Employee Type')}
                    </span>
                );
            },
        },
        {
            accessor: "total_days",
            Header: "No of days",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "status",
            Header: "Status",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => {
                if (!value) return "-";
                const statusText = capitalize(value);
                const chipStyle = getStatusChipStyle(value);

                return (
                    <span className={cn(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                        chipStyle
                    )}>
                        {statusText}
                    </span>
                );
            },
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Action",
                sortable: false,
                Cell: ({ row }) => {
                    const status = row?.status?.toLowerCase();
                    const isPending = status === 'pending';

                    const handleOpenEdit = () => {
                        navigate('/payroll/runpayrollprocess', {
                            state: {
                                fromEdit: true,
                                editDetails: row,
                                isActive: isActive
                            }
                        });
                    };

                    return (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className={cn(
                                    "hover:bg-blue-50 dark:hover:bg-gray-700",
                                    isPending
                                        ? "text-sidebar-primary dark:text-blue-400 cursor-pointer"
                                        : "text-gray-400 dark:text-gray-600 cursor-auto"
                                )}
                                onClick={handleOpenEdit}
                            >
                                <Eye className="h-4 w-4" />
                            </Button>
                        </div>
                    );
                },
            }
        ] : [])
    ];

    return (
        <>
            {/* Loading Overlay for entire screen */}
            {/* {runPayrollLoading && (
                <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                    <p className="text-xl text-gray-700 dark:text-gray-300 mb-2">Processing Payroll</p>
                    <p className="text-gray-500 dark:text-gray-400">Please wait while we prepare your payroll data...</p>
                    <div className="mt-6 w-64 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div className="bg-sidebar-primary h-2 rounded-full animate-pulse"></div>
                    </div>
                </div>
            )} */}

            <ContainerCard className="dark:bg-gray-900">
                {/* Header */}
                <ModuleTitleCard
                    Icon={FileText}
                    title={'Payroll Process'}
                    subtitle="Manage and process employee payroll calculations"
                    totalRecords={totalRecords}
                    buttonEnable={!!addPermission}
                    setOpenDialog={handleRunPayroll}
                    buttonName={'Run Payroll'}
                />

                <Reactable
                    columns={PayrollProcessColumns}
                    data={payrollProcessData}
                    setData={setPayrollProcessData}
                    setTotalCount={setTotalRecords}
                    url={PAYROLL_RUN_GET}
                    serverSide={true}
                    serverTrigger={serverTrigger}
                    tableHeight="calc(100vh - 320px)"
                    rowSize={editPermission ? "sm" : null}
                    isDraftRequired={false}
                    isCSVRequired={true}
                    isPDFRequired={true}
                />

                {/* Run Payroll Modal */}
                <DialogModal
                    open={openDialog}
                    onClose={resetForm}
                    onSave={handleSave}
                    title={"Run Payroll"}
                    closeLabel="Cancel"
                    saveLabel={"Run Payroll"}
                    loading={loading}
                    width="600px"
                >
                    <div className={cn("grid gap-6 grid-cols-1 md:grid-cols-2", "max-h-96 overflow-y-auto pr-2")}>
                        {/* Left Column */}
                        <div className="space-y-4">
                            {/* Payroll Generation Date & Time */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Payroll Generation Date & Time <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    value={formData.payroll_date}
                                    disabled
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>

                            {/* Payroll Month & Year */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Payroll Month & Year <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    allowClear
                                    inputReadOnly={true}
                                    picker="month"
                                    format="MMMM YYYY"
                                    value={formData.payroll_month_year ? moment(formData.payroll_month_year, "MMMM YYYY") : null}
                                    onChange={(date, dateString) => handleChange("payroll_month_year", dateString)}
                                    disabledDate={disabledFutureDate}
                                    placeholder="Select month and year"
                                    className="w-full"
                                />
                            </div>

                            {/* Select Department */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Department
                                </label>
                                <Select
                                    mode="multiple"
                                    size="large"
                                    value={formData.department}
                                    onChange={(value) => handleChange("department", value)}
                                    placeholder="Select departments"
                                    className="w-full"
                                >
                                    <Option key="all" value="all">All Departments</Option>
                                    {dropdownList.departmentList.map((dept) => (
                                        <Option key={dept.id} value={dept.id}>
                                            {dept.department_name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Select Employee Type */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Employee Type
                                </label>
                                <Select
                                    mode="multiple"
                                    size="large"
                                    value={formData.employee_type}
                                    onChange={(value) => handleChange("employee_type", value)}
                                    placeholder="Select employee types"
                                    className="w-full"
                                >
                                    <Option key="all" value="all">All Employee Types</Option>
                                    {dropdownList.employeeTypeList.map((type) => (
                                        <Option key={type.id} value={type.id}>
                                            {type.employee_type_name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            {/* Select Payroll Period */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Payroll Period <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    size="large"
                                    value={formData.payroll_period}
                                    onChange={(value) => handleChange("payroll_period", value)}
                                    placeholder="Select payroll period"
                                    className="w-full"
                                >
                                    {dropdownList.payrollPeriodList.map((period) => (
                                        <Option key={period.id} value={period.id}>
                                            {period.payroll_period_name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Payroll From Date */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Payroll From Date
                                </label>
                                <DatePicker
                                    value={formData.from_date ? moment(formData.from_date, "DD-MM-YYYY") : null}
                                    format="DD-MM-YYYY"
                                    className="w-full"
                                    disabled
                                />
                            </div>

                            {/* Payroll To Date */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Payroll To Date
                                </label>
                                <DatePicker
                                    value={formData.to_date ? moment(formData.to_date, "DD-MM-YYYY") : null}
                                    format="DD-MM-YYYY"
                                    className="w-full"
                                    disabled
                                />
                            </div>

                            {/* Total Days */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Total Days
                                </label>
                                <Input
                                    value={formData.total_days}
                                    disabled
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </DialogModal>
            </ContainerCard>
        </>
    );
};

export default PayrollProcess;